import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layout v1
import MenuWrapper from './modules/navigation/components/MenuWrapper/MenuWrapper';
// 030 - Banner de sesión de soporte (impersonación desde HQ)
import ImpersonationBanner from './modules/hq/components/ImpersonationBanner';
import { useImpersonationStore } from './stores/useImpersonationStore';

// Páginas
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage/UnauthorizedPage';
import getUserPermissions from './utils/shared/jwt/getUserPermissions';
import { PlanGate } from './components/PlanGate/PlanGate';
import { isFerminEnabled } from './utils/featureFlags/fermin';

// Módulos migrados (lazy loading)
const CustomersModule = lazy(() => import('./modules/customers/CustomersModule'));
const ReservationsModule = lazy(() => import('./modules/reservations/ReservationsModule'));
const PosModule = lazy(() => import('./modules/pos/PosModule'));
const StatisticsModule = lazy(() => import('./modules/statistics/StatisticsModule'));
const EmployeesModule = lazy(() => import('./modules/employees/EmployeesModule'));
const ServicesModule = lazy(() => import('./modules/services/ServicesModule'));
const WarehouseModule = lazy(() => import('./modules/warehouse/WarehouseModule'));
const BillingModule = lazy(() => import('./modules/billing/BillingModule'));
const MarketingModule = lazy(() => import('./modules/marketing/MarketingModule'));
const SuppliersModule = lazy(() => import('./modules/suppliers/SuppliersModule'));
const BillDetailPage = lazy(() => import('./modules/suppliers/pages/BillDetailPage/BillDetailPage'));
const AccountsPayablePage = lazy(() => import('./modules/suppliers/pages/AccountsPayablePage/AccountsPayablePage'));
const CreateBillPage = lazy(() => import('./modules/suppliers/pages/CreateBillPage/CreateBillPage'));
const PurchaseOrdersModule = lazy(() => import('./modules/purchase-orders/PurchaseOrdersModule'));
const LocationsModule = lazy(() => import('./modules/locations/LocationsModule'));
const ReportsModule = lazy(() => import('./modules/reports/ReportsModule'));
const RolesModule = lazy(() => import('./modules/roles/RolesModule'));
const AccountModule = lazy(() => import('./modules/account/AccountModule'));
const AnalyticsModule = lazy(() => import('./modules/analytics/AnalyticsModule'));
const ComandasModule = lazy(() => import('./modules/comandas/ComandasModule'));
const ComandaTabletModule = lazy(() => import('./modules/comanda-tablet/ComandaTabletModule'));
const CommissionsModule = lazy(() => import('./modules/commissions/CommissionsModule'));
const InternalOrdersModule = lazy(() => import('./modules/internal-orders/InternalOrdersModule'));
const FerminPage = lazy(() => import('./modules/integrations/pages/FerminPage'));
const IntegrationsModule = lazy(() => import('./modules/integrations/IntegrationsModule'));
const ReferralLandingPage = lazy(() => import('./modules/onboarding-register/pages/ReferralLandingPage'));
const ReceivablesModule = lazy(() => import('./modules/receivables/ReceivablesModule'));
const ExchangeRateModule = lazy(() => import('./modules/exchange-rate/ExchangeRateModule'));
const OnboardingRegisterModule = lazy(() => import('./modules/onboarding-register/OnboardingRegisterModule'));
const InvitationActivatePage = lazy(() => import('./modules/invitation/InvitationActivatePage'));
const MiPerfilModule = lazy(() => import('./modules/mi-perfil/MiPerfilModule'));
const VerificationModule = lazy(() => import('./modules/verification/VerificationModule'));
const LmsModule = lazy(() => import('./modules/lms/LmsModule'));
const LmsPublicCertPage = lazy(() => import('./modules/lms/pages/PublicCertVerification/PublicCertVerification'));
// 010 - Chat de soporte con IA (widget global + consola HQ)
const SupportHqModule = lazy(() => import('./modules/support/SupportHqModule'));
const HqModule = lazy(() => import('./modules/hq/HqModule'));
const SupportChatWidget = lazy(() => import('./modules/support/components/widget/SupportChatWidget'));
// 014 - Centro de ayuda (sección navegable /ayuda, header propio sin MenuWrapper)
const HelpCenterModule = lazy(() => import('./modules/help-center/HelpCenterModule'));
// 038 - Fidelización (Prikly Pass)
const LoyaltyModule = lazy(() => import('./modules/loyalty/LoyaltyModule'));
// 053 - Pantalla de cliente (WebView secundaria de la APK Prikly POS)
const CustomerDisplayPage = lazy(() => import('./modules/customer-display/CustomerDisplayPage'));

// Recuperación de contraseña (público): pantallas v2 del módulo security
const ForgotPasswordView = lazy(() =>
  import('./modules/security/pages/forgotPassword/view/ForgotPassword.view').then((m) => ({ default: m.ForgotPasswordView })),
);
const ConfirmNewPassword = lazy(() =>
  import('./modules/security/pages/forgotPassword/view/ConfirmNewPassword').then((m) => ({ default: m.ConfirmNewPassword })),
);
const CompletedPasswordChange = lazy(() =>
  import('./modules/security/pages/forgotPassword/view/CompletedPasswordChange').then((m) => ({ default: m.CompletedPasswordChange })),
);

// Placeholder para páginas en desarrollo
const ComingSoon = ({ title }: { title: string }) => (
  <div>
    <h2 style={{ color: '#45348e', fontFamily: '"Mulish", sans-serif' }}>{title}</h2>
    <p style={{ color: '#666', fontFamily: '"Mulish", sans-serif' }}>Este módulo está siendo migrado desde v1...</p>
  </div>
);

// Guard para rutas admin-only
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const permissions = getUserPermissions();
  if (!permissions?.account?.isAdmin) {
    const redirectTo = permissions?.startView || '/estadisticas';
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};

// Guard que valida acceso a un módulo por UUID en el claim `access` del JWT.
// Si el usuario no tiene el UUID, redirige a su startView (Caja para cashier,
// Estadísticas para admin, etc.). El backend tiene su propio guard espejo
// (PermissionsGuard) que valida lo mismo a nivel API.
// Mapa ruta → UUID para buscar la primera ruta accesible como fallback.
const ROUTE_UUID_MAP: Array<{ path: string; uuid: string }> = [
  { path: '/cajas', uuid: 'f363f59b-20e8-481d-83d1-72231191fbf3' },
  { path: '/estadisticas', uuid: '73d1b54a-3293-4325-9838-13beef01fa60' },
  { path: '/reservas', uuid: '48bc617b-199b-4f25-9b15-d84da7be03b3' },
  { path: '/clientes', uuid: '2e5bb676-f25c-4cd3-8897-de15782f37ba' },
  { path: '/servicios', uuid: '49400f7e-3bf1-4f6d-8f67-ea58c55f5757' },
  { path: '/almacen', uuid: '28fc22bf-3df8-4f7c-bfe8-f9082faf12b5' },
  { path: '/reportes', uuid: 'b67aa607-51b0-4e22-a070-ef38d48c2045' },
];

const findFirstAccessibleRoute = (accessKeyModules: any[]): string | null => {
  for (const route of ROUTE_UUID_MAP) {
    if (accessKeyModules.some((m: any) => m.id === route.uuid)) {
      return route.path;
    }
  }
  return null;
};

const ModuleRoute = ({
  children,
  uuid,
}: {
  children: React.ReactNode;
  uuid: string;
}) => {
  const permissions = getUserPermissions();
  const accessKeyModules = permissions?.accessKeyModules || [];
  const tieneAcceso = accessKeyModules.some((m: any) => m.id === uuid);
  if (!tieneAcceso) {
    // Buscar la primera ruta accesible: primero startView, luego fallback inteligente.
    let redirectTo = permissions?.startView || '/cajas';

    // Si el startView tampoco es accesible, buscar la primera ruta con permiso.
    const startViewRoute = ROUTE_UUID_MAP.find((r) => r.path === redirectTo);
    if (startViewRoute && !accessKeyModules.some((m: any) => m.id === startViewRoute.uuid)) {
      redirectTo = findFirstAccessibleRoute(accessKeyModules) || redirectTo;
    }

    // Protección anti-loop: si ya estamos en el destino, no redirigir.
    if (window.location.pathname.startsWith(redirectTo)) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: '"Mulish", sans-serif' }}>
          <h2 style={{ color: '#45348e' }}>Acceso restringido</h2>
          <p style={{ color: '#666' }}>No tienes permisos para acceder a esta sección. Contacta al administrador.</p>
        </div>
      );
    }
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};

// UUIDs canónicos (espejo del backend `MODULE_UUIDS`).
const MOD = {
  // Existentes
  EMPLOYEES_LIST: 'b05792dd-c9f7-4924-92d8-3d53bea8192a',
  COMMISSIONS_LIST: '90df4809-683d-479e-803e-eb01d4b26512',
  ACCOUNT_PLANS: 'c22153e1-efcb-461d-babe-0418ce5642bb',
  // Nuevos route guards
  SHOW_CUSTOMERS: '2e5bb676-f25c-4cd3-8897-de15782f37ba',
  POS_FUNCTIONS: 'f363f59b-20e8-481d-83d1-72231191fbf3',
  SHOW_CALENDAR: '48bc617b-199b-4f25-9b15-d84da7be03b3',
  VIEW_STATISTICS: '73d1b54a-3293-4325-9838-13beef01fa60',
  SHOW_SERVICES: '49400f7e-3bf1-4f6d-8f67-ea58c55f5757',
  SHOW_PRODUCTS: '28fc22bf-3df8-4f7c-bfe8-f9082faf12b5',
  MARKETING_ACCESS: 'f21678af-cdd9-4aa6-ae21-196ac82f84e6',
  SUPPLIERS_ACCESS: 'b7cb89ff-ef98-4465-8ecc-4fb27bff7ac9',
  ACCOUNTS_PAYABLE: '8cf0bb98-8c95-462f-8461-deb99943504b',
  PURCHASE_ORDERS: '912e4737-19f7-43f4-9a50-edfc5b376330',
  SHOW_LOUNGES: 'ba959108-c4ae-4167-9092-ee177883ca82',
  REPORTS_VIEW: 'b67aa607-51b0-4e22-a070-ef38d48c2045',
  SHOW_CERTIFICATES: '389c7a51-8873-4c4d-85d9-d6bb2e1cb1f8',
  ANALYTICS_VIEW: '5d6ff83a-9bb0-4e98-bdc6-59b44b891b09',
  ACCESS_COMANDAS: 'b3d8f9a2-5455-44b4-be00-62f19793b1c1',
  INTERNAL_ORDERS: 'ac5424b0-d272-45cc-b0fd-e92febd779d5',
  ORDER_TAKING: '7405e06e-90a0-436a-b3b7-88e89e5feb9c',
  // 009: LMS Certifícate — seguimiento del equipo (el espacio del alumno NO se gatea)
  LMS_TEAM: 'aa590b6a-c8e7-4b27-80b9-5fa59bb86c4e',
  // 1385: Integraciones (API externa para e-commerce)
  INTEGRATIONS: 'c5d6e7f8-9a0b-4c1d-2e3f-4a5b6c7d8e9f',
  // Cuentas por Cobrar
  RECEIVABLES_LIST: 'e8a1b2c3-cxc0-4a00-b000-000000000010',
  // Tipo de Cambio
  EXCHANGE_RATE_VIEW: 'e8a1b2c3-tc00-4a00-b000-000000000010',
  // 038: Fidelización (Prikly Pass)
  LOYALTY_VIEW: 'f414e1d8-ca35-466d-bbd3-0cc53c6a7504',
};

function App() {
  const isAuthenticated = !!localStorage.getItem('access_token');

  // 030: hidratar el estado de la sesión de soporte al arrancar (sobrevive recargas).
  const hydrateImpersonation = useImpersonationStore((s) => s.hydrate);
  useEffect(() => {
    hydrateImpersonation();
  }, [hydrateImpersonation]);

  return (
    <Routes>
      {/* Ruta de login — redirige al startView del usuario */}
      <Route path="/" element={isAuthenticated ? <Navigate to={getUserPermissions()?.startView || '/estadisticas'} /> : <LoginPage />} />

      {/* Rutas protegidas con layout v1 */}
      <Route element={isAuthenticated ? (
        <>
          {/* 030: banner de sesión de soporte (solo si hay impersonación activa) */}
          <ImpersonationBanner />
          <MenuWrapper />
          {/* 010: burbuja flotante de soporte, visible en todas las pantallas autenticadas */}
          <Suspense fallback={null}>
            <SupportChatWidget />
          </Suspense>
        </>
      ) : <Navigate to="/" />}>
        <Route path="/clientes/*" element={
          <ModuleRoute uuid={MOD.SHOW_CUSTOMERS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <CustomersModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/cajas/*" element={
          <ModuleRoute uuid={MOD.POS_FUNCTIONS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PosModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/reservas/*" element={
          <ModuleRoute uuid={MOD.SHOW_CALENDAR}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <ReservationsModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/estadisticas/*" element={
          <ModuleRoute uuid={MOD.VIEW_STATISTICS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <StatisticsModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/empleados/*" element={
          <ModuleRoute uuid={MOD.EMPLOYEES_LIST}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <EmployeesModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/servicios/*" element={
          <ModuleRoute uuid={MOD.SHOW_SERVICES}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <ServicesModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/almacen/*" element={
          <ModuleRoute uuid={MOD.SHOW_PRODUCTS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <WarehouseModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/marketing/*" element={
          <ModuleRoute uuid={MOD.MARKETING_ACCESS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="marketing"><MarketingModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/cuentas-por-pagar" element={
          <ModuleRoute uuid={MOD.ACCOUNTS_PAYABLE}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="compras"><AccountsPayablePage /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/cuentas-por-pagar/nueva-factura" element={
          <ModuleRoute uuid={MOD.ACCOUNTS_PAYABLE}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="compras"><CreateBillPage /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/proveedores/factura/:billId" element={
          <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
            <PlanGate feature="compras"><BillDetailPage /></PlanGate>
          </Suspense>
        } />
        <Route path="/proveedores/*" element={
          <ModuleRoute uuid={MOD.SUPPLIERS_ACCESS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="compras"><SuppliersModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/ordenes-compra/*" element={
          <ModuleRoute uuid={MOD.PURCHASE_ORDERS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="compras"><PurchaseOrdersModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/locales/*" element={
          <ModuleRoute uuid={MOD.SHOW_LOUNGES}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <LocationsModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/reportes/*" element={
          <ModuleRoute uuid={MOD.REPORTS_VIEW}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <ReportsModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/roles/*" element={
          <AdminRoute>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <RolesModule />
            </Suspense>
          </AdminRoute>
        } />
        <Route path="/mi-cuenta/*" element={
          <ModuleRoute uuid={MOD.ACCOUNT_PLANS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <AccountModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/facturacion/*" element={
          <ModuleRoute uuid={MOD.SHOW_CERTIFICATES}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <BillingModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/analitica/*" element={
          <ModuleRoute uuid={MOD.ANALYTICS_VIEW}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="analitica"><AnalyticsModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/comandas/*" element={
          <ModuleRoute uuid={MOD.ACCESS_COMANDAS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <ComandasModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/pedidos-internos/*" element={
          <ModuleRoute uuid={MOD.INTERNAL_ORDERS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="pedidos_internos"><InternalOrdersModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/integraciones/fermin" element={
          isFerminEnabled() ? (
            <ModuleRoute uuid={MOD.INTEGRATIONS}>
              <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
                <FerminPage />
              </Suspense>
            </ModuleRoute>
          ) : <Navigate to="/" replace />
        } />
        <Route path="/integraciones/*" element={
          <ModuleRoute uuid={MOD.INTEGRATIONS}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="integraciones"><IntegrationsModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/cuentas-por-cobrar/*" element={
          <ModuleRoute uuid={MOD.RECEIVABLES_LIST}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="cuentas_por_cobrar"><ReceivablesModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/tipo-cambio/*" element={
          <ModuleRoute uuid={MOD.EXCHANGE_RATE_VIEW}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="tipo_cambio"><ExchangeRateModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/comisiones/*" element={
          <ModuleRoute uuid={MOD.COMMISSIONS_LIST}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <PlanGate feature="comisiones"><CommissionsModule /></PlanGate>
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/fidelizacion/*" element={
          <ModuleRoute uuid={MOD.LOYALTY_VIEW}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
              <LoyaltyModule />
            </Suspense>
          </ModuleRoute>
        } />
        <Route path="/mi-perfil/*" element={
          <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
            <MiPerfilModule />
          </Suspense>
        } />
        {/* 009: LMS Certifícate — accesible a TODO empleado autenticado (sin ModuleRoute);
            el sub-acceso a Mi equipo (/certificate/equipo) se valida dentro del módulo. */}
        <Route path="/certificate/*" element={
          <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
            <LmsModule />
          </Suspense>
        } />
        {/* 010: consola HQ de soporte — solo tenant de Prikly (guard interno por /support/hq/access) */}
        <Route path="/soporte-hq" element={
          <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
            <SupportHqModule />
          </Suspense>
        } />
        {/* 012: HQ — panel interno de Prikly (ruta oculta, NO en el menú; guard interno por /hq/access) */}
        <Route path="/hq/*" element={
          <Suspense fallback={<div style={{ padding: '2rem' }}>Cargando...</div>}>
            <HqModule />
          </Suspense>
        } />
        {/* Destino de ProtectedRoute cuando falta un permiso. Sin esta ruta,
            el redirect caía al fallback "*" → "/" → startView del rol, y el
            usuario aterrizaba en su pantalla de inicio sin ninguna explicación. */}
        <Route path="/acceso-no-autorizado" element={<UnauthorizedPage />} />
      </Route>

      {/* 014: Centro de ayuda — autenticado (todos los roles), header propio sin MenuWrapper */}
      <Route path="/ayuda/*" element={
        isAuthenticated ? (
          <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
            <HelpCenterModule />
          </Suspense>
        ) : <Navigate to="/" />
      } />

      {/* Comanda Tablet — standalone sin MenuWrapper */}
      <Route path="/comanda-tablet" element={
        isAuthenticated ? (
          <ModuleRoute uuid={MOD.ORDER_TAKING}>
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
              <ComandaTabletModule />
            </Suspense>
          </ModuleRoute>
        ) : <Navigate to="/" />
      } />

      {/* 008: Verificación pública de comprobantes (destino del QR del ticket) */}
      <Route path="/v" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <VerificationModule />
        </Suspense>
      } />

      {/* 053: pantalla del cliente (display secundario de la APK Prikly POS).
          Fuera de MainLayout/guards/menú y SIN redirigir al login: sin sesión
          muestra el fondo de marca. */}
      <Route path="/pantalla-cliente" element={
        <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#1f1546' }} />}>
          <CustomerDisplayPage />
        </Suspense>
      } />

      {/* 009: Verificación pública de certificados (destino del QR del diploma) */}
      <Route path="/cert" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <LmsPublicCertPage />
        </Suspense>
      } />

      {/* 032: enlace de referido — persiste el código 60 días y lleva al registro */}
      <Route path="/r/:codigo" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <ReferralLandingPage />
        </Suspense>
      } />

      {/* Rutas públicas de onboarding (registro) */}
      <Route path="/onboarding/main/*" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <OnboardingRegisterModule />
        </Suspense>
      } />
      <Route path="/onboarding" element={<Navigate to="/onboarding/main" replace />} />

      {/* Activación de invitación de empleado (público) */}
      <Route path="/invitacion/:uuid" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <InvitationActivatePage />
        </Suspense>
      } />

      {/* Recuperar contraseña (público) — antes redirigía a v1 (Viuti) */}
      <Route path="/recuperar" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <ForgotPasswordView />
        </Suspense>
      } />
      <Route path="/recuperar/confirmar" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <ConfirmNewPassword />
        </Suspense>
      } />
      <Route path="/recuperar/completado" element={
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>}>
          <CompletedPasswordChange />
        </Suspense>
      } />

      {/* BUG-003: rutas legacy del v1 — redirigir a las rutas v2 en lugar
          de caer al fallback (que llevaba silenciosamente a /clientes). */}
      <Route path="/explorador" element={<Navigate to="/analitica" replace />} />
      <Route path="/facturacion-electronica" element={<Navigate to="/facturacion" replace />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
